"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ShieldCheck, Wrench, Flame, Clock, PhoneCall, CircleCheck as CheckCircle2, MapPin, Award, ArrowRight } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
export default function AOSmithHero() {
  const shouldReduceMotion = useReducedMotion();
  const keyPoints = [{
    text: "Factory-trained A. O. Smith technicians"
  }, {
    text: "Same-day tank and tankless replacements"
  }, {
    text: "100% genuine OEM parts and warranty protection"
  }, {
    text: "24/7 urgent emergency repair dispatch"
  }];
  const quickStats = [{
    label: "Response Time",
    value: "Under 45 Min"
  }, {
    label: "A. O. Smith Models",
    value: "Gas & Electric"
  }, {
    label: "Service Coverage",
    value: "Jonesboro & Metro"
  }];
  return <section id="aosmithwaterheaterhero" data-nav="light" className="relative overflow-hidden bg-background py-16 sm:py-20 lg:py-24 border-b border-border">
      {/* Decorative subtle background accents */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
        <div className="absolute -top-40 right-0 h-96 w-96 rounded-full bg-primary/5 blur-3xl" />
        <div className="absolute bottom-0 left-1/4 h-80 w-80 rounded-full bg-accent/10 blur-3xl" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,hsl(var(--primary)/0.03),transparent_50%)]" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-8">
          
          {/* Main Left Content */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          ease: "easeOut"
        }} className="lg:col-span-7 flex flex-col gap-6">
            {/* Brand Authority Badge */}
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="outline" className="px-3 py-1 bg-secondary text-secondary-foreground border-border text-xs font-semibold tracking-wide uppercase flex items-center gap-1.5 shadow-2xs">
                <Award className="size-3.5 text-accent" />
                Authorized A. O. Smith Specialists
              </Badge>
              <span className="hidden sm:inline-flex items-center gap-1 text-xs text-muted-foreground font-medium">
                <ShieldCheck className="size-3.5 text-chart-4" />
                Jonesboro Experienced Plumbers
              </span>
            </div>

            {/* Main Headline */}
            <h1 className="font-default text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl lg:text-5xl leading-tight">
              A. O. Smith Water Heater Repair, Installation & Replacement
            </h1>

            {/* Subtitle / Value Proposition */}
            <p className="text-base sm:text-lg text-muted-foreground max-w-2xl leading-relaxed">
              Count on Jonesboro Emergency Plumber for fast, reliable service on residential and commercial A. O. Smith hot water systems. From sudden pilot light outages and burst tanks to high-efficiency tankless upgrades, our crew provides 24/7 expert care.
            </p>

            {/* Key Service Highlights */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
              {keyPoints.map((point, index) => <div key={index} data-index={index} className="flex items-start gap-2.5">
                  <CheckCircle2 className="size-5 shrink-0 text-primary mt-0.5" />
                  <span className="text-sm font-medium text-foreground">{point.text}</span>
                </div>)}
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3.5 pt-4">
              <Button asChild size="lg" className="h-12 px-7 text-base font-semibold shadow-md bg-primary text-primary-foreground hover:bg-primary/90">
                <Link to="/contact/" className="inline-flex items-center justify-center gap-2">
                  <PhoneCall className="size-4" />
                  <span>Book Immediate Service</span>
                </Link>
              </Button>
              
              <Button asChild variant="outline" size="lg" className="h-12 px-6 text-base font-semibold border-border bg-background hover:bg-muted text-foreground">
                <Link to="/service-areas/" className="inline-flex items-center justify-center gap-2">
                  <MapPin className="size-4 text-muted-foreground" />
                  <span>Check Service Areas</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
            </div>

            {/* Trust Micro-Row */}
            <div className="pt-2 flex flex-wrap items-center gap-6 text-xs text-muted-foreground border-t border-border/80">
              <div className="flex items-center gap-2">
                <Clock className="size-4 text-accent" />
                <span>24/7 Live Dispatch</span>
              </div>
              <div className="flex items-center gap-2">
                <Flame className="size-4 text-accent" />
                <span>Gas & Electric Water Heaters</span>
              </div>
              <div className="flex items-center gap-2">
                <Wrench className="size-4 text-accent" />
                <span>Upfront Clear Estimates</span>
              </div>
            </div>
          </motion.div>

          {/* Right Showcase Card / Imagery */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1,
          scale: 1
        } : {
          opacity: 0,
          scale: 0.98
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.5,
          delay: 0.1,
          ease: "easeOut"
        }} className="lg:col-span-5">
            <Card className="relative overflow-hidden border border-border bg-card shadow-xl p-0">
              
              {/* Product Visual Header */}
              <div className="relative h-64 sm:h-72 w-full overflow-hidden bg-muted">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/ao_smith_water_heater_unit.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/ao_smith_water_heater_unit.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/ao_smith_water_heater_unit.png-wvc-sizes" alt="A. O. Smith water heater installation and maintenance service" loading="lazy" decoding="async" className="h-full w-full object-cover object-center transition-transform duration-500 hover:scale-105" />
                
                {/* Visual Overlay Tag */}
                <div className="absolute top-4 left-4">
                  <Badge className="bg-primary text-primary-foreground shadow-sm px-2.5 py-1 text-xs font-semibold">
                    A. O. Smith Specialist
                  </Badge>
                </div>

                <div className="absolute bottom-3 right-3 bg-background/90 backdrop-blur-xs px-3 py-1 rounded-md text-xs font-medium text-foreground border border-border shadow-xs">
                  ProLine & Signature Series
                </div>
              </div>

              {/* Card Body with Quick Information */}
              <CardContent className="p-6 flex flex-col gap-5">
                <div className="flex flex-col gap-1">
                  <h2 className="text-lg font-bold text-card-foreground">
                    Complete A. O. Smith System Care
                  </h2>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    We service all atmospheric gas, power vent, hybrid electric heat pump, and tankless A. O. Smith water heaters across Jonesboro and Clayton County.
                  </p>
                </div>

                {/* Stat Grid */}
                <div className="grid grid-cols-3 gap-2 rounded-lg bg-muted/60 p-3 text-center border border-border/60">
                  {quickStats.map((stat, i) => <div key={i} data-index={i} className="flex flex-col gap-0.5">
                      <span className="text-[11px] text-muted-foreground font-medium uppercase tracking-tight">
                        {stat.label}
                      </span>
                      <span className="text-xs sm:text-sm font-bold text-foreground">
                        {stat.value}
                      </span>
                    </div>)}
                </div>

                {/* Emergency Plumber Direct Callout */}
                <div className="rounded-lg border border-primary/20 bg-primary/5 p-3.5 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="flex size-9 shrink-0 items-center justify-center rounded-md bg-primary text-primary-foreground shadow-2xs">
                      <PhoneCall className="size-4" />
                    </div>
                    <div className="flex flex-col">
                      <span className="text-xs font-semibold text-foreground">Need Urgent Repair?</span>
                      <span className="text-[11px] text-muted-foreground">Local Jonesboro 24/7 service crew</span>
                    </div>
                  </div>
                  <Button asChild size="sm" variant="default" className="text-xs font-semibold shrink-0 bg-primary text-primary-foreground hover:bg-primary/90">
                    <Link to="/contact/">
                      Contact
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>

        </div>
      </div>
    </section>;
}